<script>
  import { onMount } from 'svelte';
  import { format } from 'svelte-i18n';

  import { getNotificationsContext } from '@client/components/notifications';
  import { Step } from '@client/components/steps';

  import { IntegrationDetailsForm, ProviderForm } from '@client/pages/manage/integrations/modals/shared';

  import { PluginsService } from '@client/services/plugins';

  import { appStore } from '@client/stores/app';

  const { addNotification } = getNotificationsContext();

  /**
   * Controls whether the next button in the workflow step is disabled.
   * @type {boolean}
   * @default true
   */
  export let nextDisabled = true;

  /**
   * Callback function invoked when the workflow step executes.
   * @type {Function | null}
   * @default null
   */
  export let onExecute = null;

  /**
   * Callback function invoked when the workflow step is rolled back.
   * @type {Function | null}
   * @default null
   */
  export let onRollback = null;

  /**
   * Step configuration object containing step data and metadata.
   * @type {Object}
   * @default { data: {} }
   */
  export let step = { data: {} };

  let provider = '';
  let rulename = '';
  let parameters = [];
  let fallbacktext = '';
  let currentRulenames = [];

  let isDisabled = false;

  let currentInternalStep = 0;
  let maxStepReached = 0;

  const notify = (text, type = 'success', autohide = 2500, position = 'top-right') => {
    addNotification({ text, type, autohide, position });
  };

  const handleInternalNext = (stepIndex) => {
    if (stepIndex < 1) {
      currentInternalStep = stepIndex + 1;

      if (currentInternalStep > maxStepReached) {
        maxStepReached = currentInternalStep;
      }
    }
  };

  const handleInternalBack = (stepIndex) => {
    if (stepIndex > 0) {
      currentInternalStep = stepIndex - 1;
    }
  };

  const onProviderSelect = (pluginProvider) => {
    provider = pluginProvider.value;
    handleInternalNext(0);
  };

  const getCurrentRulenames = async () => {
    const plugins = await PluginsService.getAll();
    currentRulenames = plugins.map((plugin) => plugin.rulename);
  };

  const executeCreateIntegration = async () => {
    $appStore.loading = true;

    const integrationData = {
      defaulttext: fallbacktext,
      params: parameters,
      provider: provider,
      rulename: rulename,
      status: true
    };

    const response = await PluginsService.savePlugin(integrationData);

    $appStore.loading = false;

    if (response?.status >= 400) {
      notify($format('label.FAILURE.CREATE', { values: { type: rulename } }), 'error');
      throw new Error($format('label.FAILURE.CREATE_INTEGRATION'));
    }

    notify($format('label.SUCCESS.CREATE', { values: { type: rulename } }));

    step.data = {
      id: rulename
    };

    return response;
  };

  const rollbackCreateIntegration = async () => {
    if (!step.data?.id) {
      return; // Nothing to rollback
    }

    $appStore.loading = true;

    try {
      const plugins = await PluginsService.getAll();
      const plugin = plugins.find((p) => p.rulename === step.data.id);

      if (plugin) {
        const deleteResponse = await PluginsService.deletePlugin(plugin);

        if (deleteResponse?.status >= 400) {
          throw new Error($format('label.FAILURE.ROLLBACK_INTEGRATION_DELETION'));
        }
      }
    } catch (error) {
      console.error('Failed to rollback integration creation:', error);
    } finally {
      $appStore.loading = false;
    }
  };

  onMount(() => {
    getCurrentRulenames();

    if (onExecute) {
      onExecute(executeCreateIntegration);
    }
    if (onRollback) {
      onRollback(rollbackCreateIntegration);
    }
  });

  $: {
    if (currentInternalStep === 0) {
      isDisabled = !provider;
    } else if (currentInternalStep === 1) {
      isDisabled = currentRulenames.includes(rulename) || rulename.length < 3;
    }
  }

  $: nextDisabled = currentInternalStep !== 1 || isDisabled;

  $: providerLabel = provider ? provider.charAt(0).toUpperCase() + provider.slice(1) : $format('label.NOT_SELECTED');
</script>

<div class="integration-create-form">
  <Step
    active={currentInternalStep === 0}
    disabled={false}
    title={$format('label.INTEGRATION_PROVIDER_MODAL_CHOOSE_INTEGRATION')}
    summary={providerLabel}
    onNext={currentInternalStep === 0 && provider ? () => handleInternalNext(0) : undefined}
    onStep={() => (currentInternalStep = 0)}
    nextDisabled={isDisabled}
  >
    <ProviderForm {onProviderSelect} />
  </Step>

  <Step
    active={currentInternalStep === 1}
    disabled={maxStepReached < 1}
    title={$format('label.INTEGRATION_FORM_MODAL_FILL_IN_FORM')}
    summary={rulename || $format('label.NOT_CONFIGURED')}
    onBack={currentInternalStep === 1 ? () => handleInternalBack(1) : undefined}
    onStep={() => {
      if (currentInternalStep >= 1) currentInternalStep = 1;
    }}
    nextDisabled={isDisabled}
  >
    <IntegrationDetailsForm bind:provider bind:rulename bind:parameters bind:fallbacktext bind:currentRulenames />
  </Step>
</div>

<style lang="scss">
  .integration-create-form {
    :global {
      .invalid-feedback {
        color: var(--color-red-500);
        font-size: var(--font-size-medium);
        margin-top: 0;
      }
    }
  }
</style>
